import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { SessionSummary } from '@solar/contracts';

import { Alert } from '../../components/Alert';
import { Button } from '../../components/Button';
import { PageSpinner } from '../../components/PageSpinner';
import { authApi } from '../../features/auth/auth.api';
import { useSessionQuery as useQuery } from '../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../lib/error-message';

import styles from './AppPages.module.css';

const SESSIONS_QUERY_KEY = ['auth', 'sessions'] as const;

export function SessionsPage(): React.JSX.Element {
  const queryClient = useQueryClient();

  const sessions = useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: () => authApi.listOwnSessions(),
  });

  const revoke = useMutation({
    mutationFn: (sessionId: string) => authApi.revokeOwnSession(sessionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });
    },
  });

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Active sessions</h1>
        <p className={styles.pageSubtitle}>
          Every device currently signed in to your account. Revoking a session signs that device out
          immediately.
        </p>
      </header>

      {revoke.isError && <Alert tone="error">{toDisplayMessage(revoke.error)}</Alert>}

      <section className={styles.card} aria-labelledby="sessions-heading">
        <h2 className={styles.cardTitle} id="sessions-heading">
          Signed-in devices
        </h2>

        {sessions.isPending && <PageSpinner label="Loading your sessions" />}

        {sessions.isError && <Alert tone="error">{toDisplayMessage(sessions.error)}</Alert>}

        {sessions.isSuccess && sessions.data.length === 0 && (
          <p className={styles.empty}>No active sessions.</p>
        )}

        {sessions.isSuccess && sessions.data.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <caption>Sessions are listed with the most recent activity first.</caption>
              <thead>
                <tr>
                  <th scope="col">Device</th>
                  <th scope="col">Address</th>
                  <th scope="col">Signed in</th>
                  <th scope="col">Last used</th>
                  <th scope="col">Expires</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((session) => (
                  <SessionRow
                    key={session.id}
                    session={session}
                    busy={revoke.isPending && revoke.variables === session.id}
                    onRevoke={() => {
                      revoke.mutate(session.id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function SessionRow({
  session,
  busy,
  onRevoke,
}: {
  session: SessionSummary;
  busy: boolean;
  onRevoke: () => void;
}): React.JSX.Element {
  return (
    <tr>
      <td>
        {describeUserAgent(session.userAgent)}
        {session.current && (
          <>
            {' '}
            <span className={`${styles.badge} ${styles.badgeActive}`}>This device</span>
          </>
        )}
      </td>
      <td>{session.ipAddress ?? 'Unknown'}</td>
      <td>{new Date(session.createdAt).toLocaleString()}</td>
      <td>{new Date(session.lastUsedAt).toLocaleString()}</td>
      <td>{new Date(session.expiresAt).toLocaleString()}</td>
      <td>
        <Button variant="danger" busy={busy} onClick={onRevoke}>
          {session.current ? 'Sign out this device' : 'Revoke'}
        </Button>
      </td>
    </tr>
  );
}

/**
 * Shortens a user-agent string to something a person can recognise.
 *
 * The full string is not shown: it is long, meaningless to most readers and
 * more fingerprinting detail than the page needs.
 */
function describeUserAgent(userAgent: string | null): string {
  if (userAgent === null || userAgent === '') {
    return 'Unknown device';
  }

  const browser = /(Firefox|Edg|Chrome|Safari)\/[\d.]+/.exec(userAgent)?.[1];
  const platform = /\((?<platform>[^);]+)/.exec(userAgent)?.groups?.platform;

  if (browser === undefined) {
    return platform ?? 'Unknown device';
  }

  const name = browser === 'Edg' ? 'Edge' : browser;

  return platform === undefined ? name : `${name} on ${platform}`;
}
